import { z } from 'zod';
import { CACH_XU_LY_KENH_LO_LABEL, cachXuLyKenhLoSchema, hinhThucChiaSchema, vaiTroChiaSchema, } from './chiaSe.js';
/**
 * Đối soát nhân sự theo kỳ (bước 2/3).
 *
 * Trang đối soát **không tính lại gì**: nó gọi `tinhChiaKenh` của `chiaSe.ts`
 * cho từng kênh rồi chỉ **gộp** kết quả theo người / nhóm / kênh. File này giữ
 * phần gộp đó dưới dạng hàm thuần, cộng trên **cent (số nguyên)** — cùng luật
 * làm tròn nửa lên với `thucNhan.ts` và `chiaSe.ts`.
 *
 * Bất biến mà `gopDoiSoat` phải giữ, đúng tới từng cent:
 *
 * ```
 * Σ theoNguoi.phanChia  =  Σ theoKenh.phanChia  =  Σ theoNhom.phanChia
 * ```
 *
 * Vì thế kênh **chưa gán nhóm** cũng thành một khối nhóm `teamId = null`; bỏ nó
 * đi là vế thứ ba hụt tiền mà không ai thấy.
 */
/* ------------------------------------------------------------------ */
/* Enum và nhãn                                                        */
/* ------------------------------------------------------------------ */
/**
 * Quy trình của **một người trong một kỳ**.
 *
 * `nhap` không bao giờ nằm trong database: bản ghi `staff_payouts` chỉ sinh ra
 * lúc chốt, nên "nháp" nghĩa là *chưa có phiên bản nào đang hiệu lực*.
 */
export const TRANG_THAI_DOI_SOAT = ['nhap', 'da_chot', 'da_thanh_toan'];
export const trangThaiDoiSoatSchema = z.enum(TRANG_THAI_DOI_SOAT);
export const TRANG_THAI_DOI_SOAT_LABEL = {
    nhap: 'Nháp',
    da_chot: 'Đã chốt',
    da_thanh_toan: 'Đã thanh toán',
};
export const LOAI_DIEU_CHINH = ['thuong', 'phat', 'tam_ung', 'khau_tru'];
export const loaiDieuChinhSchema = z.enum(LOAI_DIEU_CHINH);
export const LOAI_DIEU_CHINH_LABEL = {
    thuong: 'Thưởng',
    phat: 'Phạt',
    tam_ung: 'Tạm ứng',
    khau_tru: 'Khấu trừ khác',
};
/**
 * Dấu **gợi ý** của từng loại điều chỉnh, dùng để giao diện chọn sẵn `+`/`−`.
 * Số tiền vẫn lưu nguyên dấu người dùng nhập: thưởng âm (thu lại tiền thưởng
 * nhầm) là việc có thật, chặn dấu sẽ buộc kế toán ghi sai loại.
 */
export const DAU_GOI_Y_DIEU_CHINH = {
    thuong: 1,
    phat: -1,
    tam_ung: -1,
    khau_tru: -1,
};
/** Chênh lệch nhỏ hơn mức này coi như không lệch (nửa cent). */
export const NGUONG_LECH_USD = 0.005;
/* ------------------------------------------------------------------ */
/* Số học trên cent                                                    */
/* ------------------------------------------------------------------ */
function sangCent(tien) {
    return Math.round(tien * 100);
}
function sangTien(cent) {
    return Math.round(cent) / 100;
}
/** Cộng một dãy tiền **qua cent** để không tích luỹ sai số dấu phẩy động. */
function tongTien(xs) {
    return sangTien(xs.reduce((s, x) => s + sangCent(x), 0));
}
/**
 * Cộng tiền USD qua cent — dùng cho mọi tổng của trang đối soát.
 *
 * `[0.1, 0.2].reduce((a,b)=>a+b)` ra `0.30000000000000004`; cộng mười nghìn dòng
 * như thế rồi so với ảnh chụp là sinh ra "chênh lệch" không có thật.
 */
export function tongUsd(xs) {
    return tongTien(xs);
}
const DINH_DANG_TIEN = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
function tienVn(x) {
    return `${x.toLocaleString('vi-VN', DINH_DANG_TIEN)} US$`;
}
function pctVn(x) {
    return `${x.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%`;
}
/* ------------------------------------------------------------------ */
/* Một dòng kênh × vai trò                                             */
/* ------------------------------------------------------------------ */
/**
 * Số liệu **cấp kênh** của một kỳ: giống nhau cho mọi dòng chia của kênh đó.
 *
 * Tách khỏi `DongDoiSoat` để tổng cấp kênh không bị cộng trùng theo số vai trò,
 * và để kênh **không có ai được chia** vẫn góp thực nhận / chi phí / lợi nhuận
 * vào khung theo nhóm.
 */
export const kenhCoSoDoiSoatSchema = z.object({
    /** Kỳ `YYYY-MM` của dòng này. Xem theo quý thì một kênh có ba dòng. */
    period: z.string(),
    channelId: z.string(),
    tenKenh: z.string(),
    teamId: z.string().nullable(),
    tenNhom: z.string().nullable(),
    thucNhan: z.number(),
    chiPhiVanHanh: z.number(),
    loiNhuan: z.number(),
    coSoChia: z.number(),
    kenhLo: z.boolean(),
    chiaTheoThucNhan: z.boolean(),
    chinhSach: cachXuLyKenhLoSchema,
    soNgayKy: z.number().int().positive(),
    /** `null` = chưa khai. Chốt bị chặn khi kênh còn `null` (xem `chotDoiSoat`). */
    ngayBatDauVanHanh: z.string().nullable(),
});
/** Một dòng chia: một người, một vai trò, trên một kênh, trong một kỳ. */
export const dongDoiSoatSchema = z.object({
    period: z.string(),
    channelId: z.string(),
    tenKenh: z.string(),
    teamId: z.string().nullable(),
    tenNhom: z.string().nullable(),
    userId: z.string(),
    tenNguoi: z.string(),
    vaiTro: vaiTroChiaSchema,
    /** Id dòng tỷ lệ gốc (`team_leader_shares` / `channel_staff_shares`). */
    shareLineId: z.string(),
    hinhThuc: hinhThucChiaSchema,
    pct: z.number(),
    coDinhUsd: z.number(),
    soNgayHieuLuc: z.number().int().nonnegative(),
    soNgayKy: z.number().int().positive(),
    phanTheoPct: z.number(),
    phanCoDinh: z.number(),
    phanChia: z.number(),
    /** Công thức để giao diện in nguyên văn, xem {@link congThucDong}. */
    congThuc: z.string(),
});
/**
 * Công thức của một dòng, viết đúng như giao diện phải hiện.
 *
 * ```
 * 10% × 8.754,82 US$
 * 15/31 ngày × 12% × 8.754,82 US$
 * Kênh lỗ — 10% × thực nhận 100,00 US$
 * 16/31 ngày × 120,00 US$/tháng
 * ```
 *
 * Cơ sở chia in **nguyên số mà % nhân vào**, không in lợi nhuận: hai số đó khác
 * nhau đúng ở các kỳ lỗ, và đó là lúc người đọc cần biết mình đang được chia
 * trên cái gì.
 */
export function congThucDong(dong, kenh) {
    const theoNgay = dong.soNgayHieuLuc < dong.soNgayKy;
    const phanNgay = theoNgay ? `${String(dong.soNgayHieuLuc)}/${String(dong.soNgayKy)} ngày` : null;
    /*
     * Cơ sở bằng 0 thì cả dòng bằng 0 (kể cả phần cố định) — xem `tinhChiaKenh`.
     * Nói thẳng lý do, vì một dòng 10% ra 0,00 US$ trông như lỗi.
     */
    if (dong.hinhThuc !== 'co_dinh' && kenh.coSoChia === 0) {
        return `${CACH_XU_LY_KENH_LO_LABEL[kenh.chinhSach]} — ${pctVn(dong.pct)} × ${tienVn(0)}`;
    }
    if (dong.hinhThuc === 'co_dinh' && kenh.coSoChia === 0) {
        return `${CACH_XU_LY_KENH_LO_LABEL[kenh.chinhSach]} — ${tienVn(0)}`;
    }
    const phan = [];
    if (dong.hinhThuc !== 'co_dinh') {
        const coSo = kenh.chiaTheoThucNhan
            ? `thực nhận ${tienVn(kenh.coSoChia)}`
            : tienVn(kenh.coSoChia);
        phan.push([phanNgay, pctVn(dong.pct), coSo].filter(Boolean).join(' × '));
    }
    if (dong.hinhThuc !== 'pct') {
        phan.push([phanNgay, `${tienVn(dong.coDinhUsd)}/tháng`].filter(Boolean).join(' × '));
    }
    const than = phan.join(' + ');
    return kenh.chiaTheoThucNhan ? `Kênh lỗ — ${than}` : than;
}
/* ------------------------------------------------------------------ */
/* Kết quả gộp                                                         */
/* ------------------------------------------------------------------ */
export const kenhDoiSoatSchema = kenhCoSoDoiSoatSchema.extend({
    /** Tổng chia của kênh, cộng mọi vai trò. */
    phanChia: z.number(),
    /** Lợi nhuận − tổng chia. Âm khi kênh lỗ mà vẫn chia. */
    congTyGiu: z.number(),
    dong: z.array(dongDoiSoatSchema),
});
export const nhomDoiSoatSchema = z.object({
    /** `null` = khối "Chưa gán nhóm". */
    teamId: z.string().nullable(),
    tenNhom: z.string(),
    /** Số kênh **khác nhau**, không phải số dòng kênh × kỳ. */
    soKenh: z.number().int().nonnegative(),
    thucNhan: z.number(),
    chiPhiVanHanh: z.number(),
    loiNhuan: z.number(),
    phanChia: z.number(),
    congTyGiu: z.number(),
});
export const nguoiDoiSoatSchema = z.object({
    userId: z.string(),
    tenNguoi: z.string(),
    avatarColor: z.string(),
    /** Các vai trò người này giữ trong kỳ, theo thứ tự `VAI_TRO_CHIA`. */
    vaiTro: z.array(vaiTroChiaSchema),
    soKenh: z.number().int().nonnegative(),
    phanChia: z.number(),
    dong: z.array(dongDoiSoatSchema),
});
export const tongDoiSoatSchema = z.object({
    soKenh: z.number().int().nonnegative(),
    soNguoi: z.number().int().nonnegative(),
    thucNhan: z.number(),
    chiPhiVanHanh: z.number(),
    loiNhuan: z.number(),
    phanChia: z.number(),
    congTyGiu: z.number(),
});
export const ketQuaGopSchema = z.object({
    theoNguoi: z.array(nguoiDoiSoatSchema),
    theoNhom: z.array(nhomDoiSoatSchema),
    theoKenh: z.array(kenhDoiSoatSchema),
    tong: tongDoiSoatSchema,
});
const HANG_VAI_TRO = { truong_nhom: 0, quan_ly: 1, editor: 2 };
/**
 * Gộp các dòng chia của một kỳ theo ba chiều.
 *
 * Mọi tổng cộng **qua cent**, nên ba vế của bất biến ở đầu file khớp tuyệt đối
 * chứ không "khớp tới 0,01". Dòng có kênh không nằm trong `kenh` bị **bỏ qua**
 * ở tổng cấp kênh/nhóm (không có số liệu kênh để cộng) nhưng vẫn vào tổng theo
 * người — trường hợp đó là lỗi gọi hàm, nên hàm giữ tiền của người lại cho dễ
 * thấy thay vì âm thầm làm mất.
 */
export function gopDoiSoat(dau) {
    const { kenh, dong } = dau;
    const mau = dau.mauNguoi ?? new Map();
    /*
     * Khoá cấp kênh là **kênh × kỳ**, không phải kênh. Xem theo quý là ba kỳ
     * tháng gộp lại, mà mỗi tháng có thực nhận / chi phí / lợi nhuận riêng; khoá
     * theo kênh thôi thì hai tháng sau ghi đè tháng đầu. Nhờ khoá này, xem tháng
     * và xem quý đi **cùng một đường mã**.
     */
    const dongTheoKenh = new Map();
    for (const d of dong) {
        const khoa = `${d.channelId}|${d.period}`;
        const cu = dongTheoKenh.get(khoa);
        if (cu)
            cu.push(d);
        else
            dongTheoKenh.set(khoa, [d]);
    }
    const theoKenh = kenh
        .map((k) => {
        const cua = dongTheoKenh.get(`${k.channelId}|${k.period}`) ?? [];
        const phanChia = tongTien(cua.map((d) => d.phanChia));
        return {
            ...k,
            phanChia,
            congTyGiu: sangTien(sangCent(k.loiNhuan) - sangCent(phanChia)),
            dong: cua,
        };
    })
        .sort((a, b) => a.tenKenh.localeCompare(b.tenKenh, 'vi') || a.period.localeCompare(b.period));
    /* ------------------------------ Theo nhóm ------------------------- */
    /** Kênh khác nhau của từng nhóm — đếm kênh, không đếm dòng kênh × kỳ. */
    const kenhCuaNhom = new Map();
    const theoNhomMap = new Map();
    for (const k of theoKenh) {
        const khoa = k.teamId ?? '';
        const tap = kenhCuaNhom.get(khoa) ?? new Set();
        tap.add(k.channelId);
        kenhCuaNhom.set(khoa, tap);
        const cu = theoNhomMap.get(khoa);
        if (cu) {
            cu.thucNhan = sangTien(sangCent(cu.thucNhan) + sangCent(k.thucNhan));
            cu.chiPhiVanHanh = sangTien(sangCent(cu.chiPhiVanHanh) + sangCent(k.chiPhiVanHanh));
            cu.loiNhuan = sangTien(sangCent(cu.loiNhuan) + sangCent(k.loiNhuan));
            cu.phanChia = sangTien(sangCent(cu.phanChia) + sangCent(k.phanChia));
            cu.congTyGiu = sangTien(sangCent(cu.congTyGiu) + sangCent(k.congTyGiu));
            continue;
        }
        theoNhomMap.set(khoa, {
            teamId: k.teamId,
            tenNhom: k.tenNhom ?? 'Chưa gán nhóm',
            soKenh: 0,
            thucNhan: k.thucNhan,
            chiPhiVanHanh: k.chiPhiVanHanh,
            loiNhuan: k.loiNhuan,
            phanChia: k.phanChia,
            congTyGiu: k.congTyGiu,
        });
    }
    for (const [khoa, n] of theoNhomMap)
        n.soKenh = kenhCuaNhom.get(khoa)?.size ?? 0;
    /*
     * "Chưa gán nhóm" xuống cuối; còn lại theo tên, `vi` để "Đ" không nhảy sau
     * "Z".
     */
    const theoNhom = [...theoNhomMap.values()].sort((a, b) => {
        if (a.teamId === null)
            return 1;
        if (b.teamId === null)
            return -1;
        return a.tenNhom.localeCompare(b.tenNhom, 'vi');
    });
    /* ------------------------------ Theo người ------------------------ */
    const theoNguoiMap = new Map();
    for (const d of dong) {
        const cu = theoNguoiMap.get(d.userId);
        if (cu) {
            cu.dong.push(d);
            continue;
        }
        theoNguoiMap.set(d.userId, {
            userId: d.userId,
            tenNguoi: d.tenNguoi,
            avatarColor: mau.get(d.userId) ?? '#667085',
            vaiTro: [],
            soKenh: 0,
            phanChia: 0,
            dong: [d],
        });
    }
    for (const n of theoNguoiMap.values()) {
        n.vaiTro = [...new Set(n.dong.map((d) => d.vaiTro))].sort((a, b) => HANG_VAI_TRO[a] - HANG_VAI_TRO[b]);
        n.soKenh = new Set(n.dong.map((d) => d.channelId)).size;
        n.phanChia = tongTien(n.dong.map((d) => d.phanChia));
        /*
         * Hoà nhau thì giữ nguyên thứ tự đầu vào — `Array.sort` ổn định, và đầu vào
         * đã xếp theo `hieuLucTu` tăng dần. Nhờ vậy một kênh đổi tỷ lệ giữa kỳ hiện
         * **hai dòng theo đúng thứ tự thời gian**: dòng 10% của nửa đầu tháng rồi
         * dòng 12% của nửa sau.
         */
        n.dong.sort((a, b) => a.tenKenh.localeCompare(b.tenKenh, 'vi') ||
            a.period.localeCompare(b.period) ||
            HANG_VAI_TRO[a.vaiTro] - HANG_VAI_TRO[b.vaiTro]);
    }
    const theoNguoi = [...theoNguoiMap.values()].sort((a, b) => b.phanChia - a.phanChia || a.tenNguoi.localeCompare(b.tenNguoi, 'vi'));
    return {
        theoNguoi,
        theoNhom,
        theoKenh,
        tong: {
            /** Kênh **khác nhau**: xem theo quý thì một kênh vẫn là một kênh. */
            soKenh: new Set(theoKenh.map((k) => k.channelId)).size,
            soNguoi: theoNguoi.length,
            thucNhan: tongTien(theoKenh.map((k) => k.thucNhan)),
            chiPhiVanHanh: tongTien(theoKenh.map((k) => k.chiPhiVanHanh)),
            loiNhuan: tongTien(theoKenh.map((k) => k.loiNhuan)),
            phanChia: tongTien(theoKenh.map((k) => k.phanChia)),
            congTyGiu: tongTien(theoKenh.map((k) => k.congTyGiu)),
        },
    };
}
/* ------------------------------------------------------------------ */
/* Điều chỉnh                                                          */
/* ------------------------------------------------------------------ */
export const dieuChinhSchema = z.object({
    id: z.string(),
    period: z.string(),
    userId: z.string(),
    loai: loaiDieuChinhSchema,
    /** USD, âm hoặc dương. */
    usd: z.number(),
    lyDo: z.string(),
    nguoiTao: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/** Tổng điều chỉnh, cộng qua cent. */
export function tongDieuChinh(ds) {
    return tongTien(ds.map((d) => d.usd));
}
/* ------------------------------------------------------------------ */
/* Ảnh chụp và quy trình                                               */
/* ------------------------------------------------------------------ */
/**
 * Một dòng của ảnh chụp lúc chốt.
 *
 * Khác `DongDoiSoat` ở chỗ **tự chứa** số liệu cấp kênh: ảnh chụp phải đọc được
 * một mình, kể cả khi kênh đã bị đổi tên, chuyển nhóm hay xoá.
 */
export const dongAnhChupSchema = dongDoiSoatSchema.extend({
    thucNhan: z.number(),
    chiPhiVanHanh: z.number(),
    loiNhuan: z.number(),
    coSoChia: z.number(),
    kenhLo: z.boolean(),
    chiaTheoThucNhan: z.boolean(),
    chinhSach: cachXuLyKenhLoSchema,
});
/** Chênh lệch giữa ảnh chụp lúc chốt và số tính lại từ dữ liệu hiện tại. */
export const lechAnhChupSchema = z.object({
    phanChiaCu: z.number(),
    phanChiaMoi: z.number(),
    chenh: z.number(),
});
/** Một lần chốt của một người trong một kỳ. */
export const phienBanChotSchema = z.object({
    id: z.string(),
    period: z.string(),
    phienBan: z.number().int().positive(),
    /** `false` = đã bị thay bởi một lần chốt sau. */
    hieuLuc: z.boolean(),
    trangThai: trangThaiDoiSoatSchema,
    tongPhanChia: z.number(),
    tongDieuChinh: z.number(),
    phaiTra: z.number(),
    chotAt: z.iso.datetime(),
    nguoiChot: z.string().nullable(),
    thanhToanAt: z.iso.datetime().nullable(),
    /** Khoá ngày `YYYY-MM-DD` của lần chuyển tiền. */
    thanhToanNgay: z.string().nullable(),
    thanhToanGhiChu: z.string().nullable(),
    thanhToanTepChungTu: z.string().nullable(),
    /** Lý do **mở lại** phiên bản này (có khi nó đã bị thay). */
    moLaiAt: z.iso.datetime().nullable(),
    moLaiLyDo: z.string().nullable(),
    nguoiMoLai: z.string().nullable(),
    /* Quy đổi VND và thuế TNCN (bước 3). `null` = chưa nhập tỷ giá. */
    tyGiaVnd: z.number().nullable(),
    phaiTraVnd: z.number().nullable(),
    thueTncn: z.number().nullable(),
    thueTncnPct: z.number().nullable(),
    /**
     * `LoaiThueTncn`, khai là `string` ở đây để tránh vòng import: `thue.ts` cần
     * `dongAnhChupSchema` của file này, nên file này không import ngược được.
     * Giao diện ép kiểu khi dùng.
     */
    loaiThue: z.string().nullable(),
    thueGhiDe: z.boolean(),
    thucChuyenVnd: z.number().nullable(),
});
/** Một người trong một kỳ: số tính trực tiếp, ảnh chụp, điều chỉnh, trạng thái. */
export const doiSoatNguoiSchema = z.object({
    userId: z.string(),
    tenNguoi: z.string(),
    emailNguoi: z.string(),
    avatarColor: z.string(),
    nguoiDaLuuTru: z.boolean(),
    vaiTro: z.array(vaiTroChiaSchema),
    soKenh: z.number().int().nonnegative(),
    trangThai: trangThaiDoiSoatSchema,
    /**
     * Phần chia **đang hiệu lực**: số của ảnh chụp khi đã chốt, số tính trực tiếp
     * khi còn nháp.
     */
    phanChia: z.number(),
    dieuChinh: z.number(),
    phaiTra: z.number(),
    /** Các dòng kênh × vai trò *đang hiệu lực* (ảnh chụp hoặc tính trực tiếp). */
    dong: z.array(dongAnhChupSchema),
    khoanDieuChinh: z.array(dieuChinhSchema),
    /** `null` khi còn nháp; khác `null` thì có cảnh báo lệch số liệu nguồn. */
    lech: lechAnhChupSchema.nullable(),
    /** Mọi lần chốt, mới nhất trước. Rỗng khi chưa chốt lần nào. */
    phienBan: z.array(phienBanChotSchema),
    /**
     * Kênh của người này **chưa khai ngày bắt đầu vận hành**.
     *
     * Rỗng là chốt được. Khác rỗng thì chi phí của các kênh đó đang trừ **toàn
     * bộ** (không tách được khoản tiêu trước lúc kênh đăng video), nên số chưa
     * đáng tin để đóng băng — server chặn chốt và nêu tên kênh.
     */
    kenhThieuNgayVanHanh: z.array(z.object({ channelId: z.string(), tenKenh: z.string() })),
});
export const doiSoatKySchema = z.object({
    /** `YYYY-MM`, hoặc tháng đầu của quý khi xem theo quý. */
    period: z.string(),
    /** `YYYY-Qn` khi đang xem theo quý, `null` khi xem theo tháng. */
    quy: z.string().nullable(),
    /** Các kỳ tháng đã gộp vào kết quả này (1 phần tử khi xem theo tháng). */
    cacKy: z.array(z.string()),
    kyTu: z.string(),
    kyDen: z.string(),
    /** `true` khi xem theo quý: chỉ xem, không chốt / điều chỉnh / thanh toán. */
    chiXem: z.boolean(),
    nguoi: z.array(doiSoatNguoiSchema),
    theoNhom: z.array(nhomDoiSoatSchema),
    theoKenh: z.array(kenhDoiSoatSchema),
    tong: tongDoiSoatSchema,
    /** Thẻ tổng của quy trình. */
    tongQuyTrinh: z.object({
        phanChia: z.number(),
        dieuChinh: z.number(),
        phaiTra: z.number(),
        daTra: z.number(),
        conLai: z.number(),
        demNhap: z.number().int().nonnegative(),
        demDaChot: z.number().int().nonnegative(),
        demDaThanhToan: z.number().int().nonnegative(),
        demLech: z.number().int().nonnegative(),
    }),
});
/* ------------------------------------------------------------------ */
/* Input của endpoint                                                  */
/* ------------------------------------------------------------------ */
const KY_THANG = /^\d{4}-(0[1-9]|1[0-2])$/;
const KY_QUY = /^\d{4}-Q[1-4]$/;
export const kyThangSchema = z.string().regex(KY_THANG, 'Kỳ phải dạng YYYY-MM');
/**
 * Kỳ xem: tháng **hoặc** quý, không cả hai.
 *
 * Quý là ba kỳ tháng gộp lại và **chỉ xem** — quy trình chốt/thanh toán vẫn
 * chạy theo tháng, vì tiền trả theo tháng.
 */
export const doiSoatQuerySchema = z
    .object({
    period: kyThangSchema.optional(),
    quy: z.string().regex(KY_QUY, 'Quý phải dạng YYYY-Q1..Q4').optional(),
})
    .refine((x) => !(x.period && x.quy), {
    message: 'Chọn tháng hoặc quý, không chọn cả hai',
    path: ['quy'],
});
/**
 * Chỉ **một con số**: còn bao nhiêu người chưa chốt trong kỳ.
 *
 * Badge "N nháp" cạnh mục menu Đối soát trước đây gọi `GET /team/doi-soat` —
 * một lượt tính doanh thu cho **mỗi kênh** chỉ để lấy một con số. Endpoint
 * riêng chỉ đọc bảng tỷ lệ và bảng ảnh chụp (xem `demBanNhapChuaChot`).
 */
export const soNhapDoiSoatSchema = z.object({
    period: kyThangSchema,
    demNhap: z.number().int().nonnegative(),
});
/** `2026-Q4` → `['2026-10','2026-11','2026-12']`. */
export function thangCuaQuy(quy) {
    const [namChu, qChu] = quy.split('-Q');
    const nam = Number(namChu);
    const q = Number(qChu);
    if (!Number.isInteger(nam) || !Number.isInteger(q) || q < 1 || q > 4)
        return [];
    return [0, 1, 2].map((i) => `${String(nam)}-${String((q - 1) * 3 + 1 + i).padStart(2, '0')}`);
}
/** `2026-11` → `2026-Q4`. */
export function quyCuaThang(period) {
    const [nam, thang] = period.split('-');
    const q = Math.floor((Number(thang) - 1) / 3) + 1;
    return `${nam ?? '?'}-Q${String(q)}`;
}
export const luuDieuChinhSchema = z.object({
    period: kyThangSchema,
    userId: z.string().min(1),
    loai: loaiDieuChinhSchema,
    /**
     * USD, âm hoặc dương, **khác 0**. Không chặn dấu theo loại: thu lại tiền
     * thưởng đã trả nhầm là một dòng `thuong` âm.
     */
    usd: z
        .number()
        .refine((x) => Math.abs(x) >= 0.01, { message: 'Số tiền phải khác 0' })
        .refine((x) => Math.abs(x) <= 1_000_000, { message: 'Số tiền quá lớn' }),
    lyDo: z.string().trim().min(3, 'Phải ghi lý do').max(500),
});
export const moLaiDoiSoatSchema = z.object({
    lyDo: z.string().trim().min(5, 'Phải ghi lý do mở lại').max(500),
});
const KHOA_NGAY = /^\d{4}-\d{2}-\d{2}$/;
export const danhDauThanhToanSchema = z.object({
    /** Ngày chuyển tiền, khoá ngày `YYYY-MM-DD`. Thiếu thì lấy hôm nay. */
    ngay: z.string().regex(KHOA_NGAY, 'Ngày phải dạng YYYY-MM-DD').optional(),
    ghiChu: z.string().trim().max(500).nullish(),
    /** Tên tệp chứng từ đã tải lên; app chỉ lưu đường dẫn. */
    tepChungTu: z.string().trim().max(400).nullish(),
    /*
     * Quy đổi VND nhập ngay ở bước này (bước 3). Thiếu thì giữ số đã nhập lúc xem
     * trước sau khi chốt; vẫn trống thì biên bản để trống phần VND.
     */
    tyGiaVnd: z.number().positive().max(1_000_000).optional(),
    thueTncn: z.number().min(0).max(1_000_000_000).optional(),
    thueGhiDeLyDo: z.string().trim().max(500).optional(),
});
/* ------------------------------------------------------------------ */
/* Nhắc ở trang Tổng quan                                              */
/* ------------------------------------------------------------------ */
/**
 * Từ ngày này của **tháng sau** thì tổng quan mới nhắc "còn N bản nháp chưa
 * chốt": doanh thu YouTube của kỳ chỉ đủ số sau vài ngày đầu tháng, nhắc sớm
 * hơn là nhắc chốt trên số liệu chưa xong.
 */
export const NGAY_NHAC_CHOT = 5;
/** `2026-11-05` → kỳ `2026-10` cần nhắc; chưa tới ngày nhắc thì `null`. */
export function kyCanNhacChot(homNay) {
    if (!KHOA_NGAY.test(homNay))
        return null;
    const [nam, thang, ngay] = homNay.split('-').map(Number);
    if (ngay < NGAY_NHAC_CHOT)
        return null;
    const truoc = new Date(Date.UTC(nam, thang - 2, 1));
    return `${String(truoc.getUTCFullYear())}-${String(truoc.getUTCMonth() + 1).padStart(2, '0')}`;
}
/** `2026-10` → `Kỳ 10/2026`. */
export function nhanKyDoiSoat(period) {
    const [nam, thang] = period.split('-');
    return `Kỳ ${thang ?? '?'}/${nam ?? '?'}`;
}
//# sourceMappingURL=doiSoat.js.map